import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { LoadingSpinner, PrimaryBlueButton, CustomizableSelect, Severity } from "@velaro/velaro-component-lib";
import useAIConfigs from "@/hooks/useAIConfigs";
import useToastAlert from "@/hooks/useToastAlert";
import useApi from "@/hooks/useApi";
import AINewSettingsTabs from "./AINewSettingsTabs";
import { ScraperJob } from "./types";
import useSite from "@/hooks/useSite";

// Local development uses Python scraper with OPENAI_API_KEY
// Staging/Production uses velaro-messaging API with Azure OpenAI
const IS_LOCAL_DEV = import.meta.env.DEV && !import.meta.env.VITE_SCRAPER_API_URL;
const SCRAPER_API_URL = import.meta.env.VITE_SCRAPER_API_URL || "http://localhost:8000/api/v1";
const SCRAPER_API_KEY = import.meta.env.VITE_SCRAPER_API_KEY || "";

export default function AINewSettingsPlayground() {
  const { id } = useParams();
  const { aiConfigurations, loading } = useAIConfigs();
  const toastAlert = useToastAlert();
  const { site } = useSite();
  const api = useApi();

  const [jobs, setJobs] = useState<ScraperJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [querying, setQuerying] = useState(false);

  const config = aiConfigurations.find((c) => c.id === parseInt(id!));

  const getHeaders = () => ({
    "Content-Type": "application/json",
    "X-Service-Key": SCRAPER_API_KEY,
    "X-Site-Id": site?.siteId?.toString() || "0"
  });

  // Fetch available scraper jobs (only for local development)
  useEffect(() => {
    // Skip fetching jobs for staging/production - not needed
    if (!IS_LOCAL_DEV) {
      setLoadingJobs(false);
      return;
    }

    // Only fetch if site is loaded
    if (!site?.siteId || site.siteId <= 0) {
      setLoadingJobs(false);
      return;
    }

    const fetchJobs = async () => {
      try {
        const response = await fetch(`${SCRAPER_API_URL}/jobs`, {
          headers: getHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          // Filter to completed jobs with at least 1 page
          const completedJobs = (data || []).filter(
            (job: ScraperJob) => job.status === "completed" && job.pages_scraped >= 1
          );
          setJobs(completedJobs);
          if (completedJobs.length > 0) {
            setSelectedJobId(completedJobs[0].job_id);
          }
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, [site?.siteId]);

  const selectedJob = jobs.find((j) => j.job_id === selectedJobId);

  const handleQuery = async () => {
    if (!query.trim()) {
      toastAlert.displayToast(Severity.Error, "Query cannot be empty");
      return;
    }

    // For local dev, require scraper job selection
    if (IS_LOCAL_DEV && !selectedJobId) {
      toastAlert.displayToast(Severity.Error, "Please select a scraper job first");
      return;
    }

    // For staging/production, require AI config with index
    if (!IS_LOCAL_DEV && !config?.indexName) {
      toastAlert.displayToast(Severity.Error, "No index configured for this chatbot. Configure an index in the General settings.");
      return;
    }

    setQuerying(true);
    setResult(null);

    try {
      if (IS_LOCAL_DEV) {
        // Local development: Use Python scraper with OPENAI_API_KEY
        let systemPrompt = "";
        if (config?.prompt) {
          systemPrompt = config.prompt;
        }
        if (config?.tone) {
          systemPrompt += systemPrompt ? ` Tone: ${config.tone}` : `Tone: ${config.tone}`;
        }

        const response = await fetch(`${SCRAPER_API_URL}/chat`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({
            job_id: selectedJobId,
            question: query,
            model: config?.aiModel || "gpt-4o-mini",
            system_prompt: systemPrompt || undefined
          })
        });

        const data = await response.json();

        if (data.success) {
          setResult(data.answer);
        } else {
          toastAlert.displayToast(Severity.Error, data.error || "Failed to get response");
          setResult(`Error: ${data.error || "Failed to get response"}`);
        }
      } else {
        // Staging/Production: Use velaro-messaging API with Azure OpenAI
        const response = await api.messaging.post("AzureIndexes/ChatQuery", {
          aiConfigId: parseInt(id!),
          query: query
        });

        const data = await response.json();

        if (data.success) {
          setResult(data.answer);
        } else {
          toastAlert.displayToast(Severity.Error, data.error || "Failed to get response");
          setResult(`Error: ${data.error || "Failed to get response"}`);
        }
      }
    } catch (error) {
      console.error("Query error:", error);
      toastAlert.displayToast(Severity.Error, "An error occurred while querying");
    } finally {
      setQuerying(false);
    }
  };

  const handleClear = () => {
    setResult(null);
    setQuery("");
  };

  const handleJobChange = (jobId: string) => {
    setSelectedJobId(jobId);
    setResult(null);
    setQuery("");
  };

  if (loading || loadingJobs) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id!} />

      <div className="bg-white p-6 border rounded-2xl shadow-sm w-[750px] self-center mt-4">
        <div className="flex flex-col gap-2">
          {/* Knowledge Base - different UI for local dev vs staging/production */}
          {IS_LOCAL_DEV ? (
            <>
              {/* Local Dev: Job Selection */}
              <div className="py-3">
                <div className="font-semibold text-sm pb-1">Knowledge Base (Local Dev)</div>
                <div className="text-slate-500 text-sm pb-2">
                  Select a completed scraper job to use as the knowledge base for testing
                </div>
                {jobs.length === 0 ? (
                  <div className="text-slate-500 text-sm p-3 bg-slate-50 rounded-md">
                    No completed scraper jobs available.
                    Create and complete a scraper job in Chatbot Training first.
                  </div>
                ) : (
                  <CustomizableSelect
                    options={jobs.map((job) => ({
                      label: job.name,
                      sublabel: `${job.pages_scraped} pages`,
                      action: () => handleJobChange(job.job_id)
                    }))}
                    defaultOption={{
                      label: selectedJob?.name || "Select a job",
                      sublabel: selectedJob ? `${selectedJob.pages_scraped} pages` : "",
                      action: () => {}
                    }}
                  />
                )}
              </div>

              {/* Pages Indexed */}
              <div className="py-3">
                <div className="font-semibold text-sm pb-1">Pages Indexed</div>
                <div className="text-slate-500 text-sm">
                  {selectedJob ? `${selectedJob.pages_scraped} pages` : "—"}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Staging/Production: Show configured index */}
              <div className="py-3">
                <div className="font-semibold text-sm pb-1">Knowledge Base</div>
                <div className="text-slate-500 text-sm pb-2">
                  Using the Azure Search index configured for this chatbot
                </div>
                <div className="text-sm p-3 bg-slate-50 rounded-md">
                  {config?.indexName ? (
                    <span className="font-medium">{config.indexName}</span>
                  ) : (
                    <span className="text-amber-600">
                      No index configured. Go to General settings to configure an index.
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Test Query */}
          <div className="py-3">
            <div className="font-semibold text-sm pb-1">Test Query</div>
            <div className="text-slate-500 text-sm pb-2">
              Enter a question to test the AI response using the selected knowledge base
            </div>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleQuery();
                }
              }}
              placeholder="Ask a question about the scraped content..."
              rows={3}
              disabled={IS_LOCAL_DEV ? !selectedJobId : !config?.indexName}
              className="w-full p-2 border rounded-md text-sm resize-none disabled:bg-slate-100 disabled:cursor-not-allowed"
            />
            <div className="text-xs text-slate-400 mt-1">
              Press Enter to send, Shift+Enter for new line
            </div>
          </div>

          <div className="flex gap-2">
            <PrimaryBlueButton
              label={querying ? "Thinking..." : "Test Query"}
              onClick={handleQuery}
              disabled={querying || !query.trim() || (IS_LOCAL_DEV ? !selectedJobId : !config?.indexName)}
            />
            {(result !== null || query) && (
              <button
                onClick={handleClear}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* AI Response */}
          {result !== null && (
            <div className="pt-4 flex flex-col">
              <div className="font-semibold text-sm pb-2">AI Response:</div>
              <div className="bg-slate-50 border rounded-lg p-4 max-h-96 overflow-auto">
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{result}</div>
              </div>
            </div>
          )}

          {result === null && !querying && (IS_LOCAL_DEV ? selectedJobId : config?.indexName) && (
            <div className="pt-4 text-center text-slate-400 text-sm">
              Enter a question and click "Test Query" to see the AI response
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="w-[750px] self-center mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        <strong>Testing Mode:</strong> Results are not saved. Use this to verify your chatbot responds correctly to questions based on the scraped content.
        {IS_LOCAL_DEV && (
          <div className="mt-2 text-xs text-amber-600">
            Local development mode: Using Python scraper with direct OpenAI API.
          </div>
        )}
      </div>
    </div>
  );
}
